/**
 * Message Requests section (#3039).
 *
 * Drives the Primary/Requests inbox split UI:
 *   - inline preview: collapse (preview <-> avatar-stack banner, persisted),
 *     per-row Accept (-> Primary) / Decline, row body opens the conversation.
 *   - full "See all" view: fetched into the PM main panel as an overlay, with
 *     Accept / Decline / Block per row, bulk Delete all, and a back button.
 * Per-row + bulk actions call the /v1 REST endpoints through window.ApiClient.
 * Reading a Request thread sends no read receipt (suppressed server-side).
 */
(function () {
  'use strict';

  // Set when a full-view action mutated server state, so closing the view
  // reloads to resync the Primary list and the inline preview count.
  var fullViewChanged = false;

  function section() {
    return document.getElementById('message-requests-section');
  }

  function fullView() {
    return document.getElementById('message-requests-view');
  }

  function t(key, fallback) {
    if (typeof window.t === 'function') {
      var s = window.t(key);
      if (s && s !== key) {
        return s;
      }
    }
    return fallback;
  }

  function setBusy(el, busy) {
    if (!el) return;
    el.style.opacity = busy ? '0.5' : '';
    el.style.pointerEvents = busy ? 'none' : '';
  }

  function apiPost(path) {
    if (!window.ApiClient || typeof window.ApiClient.request !== 'function') {
      return Promise.reject(new Error('ApiClient unavailable'));
    }
    return window.ApiClient.request(path, { method: 'POST' }).then(function (resp) {
      if (!resp || !resp.ok) {
        throw new Error('Request failed: ' + (resp ? resp.status : 'no response'));
      }
      return resp;
    });
  }

  function actionError() {
    alert(t('common.pm.requests_action_error', 'Could not complete that action. Please try again.'));
  }

  function confirmThen(opts, onConfirm) {
    if (window.ConfirmModal && typeof window.ConfirmModal.show === 'function') {
      window.ConfirmModal.show({
        title: opts.title,
        message: opts.message,
        confirmText: opts.confirmText,
        type: 'danger',
        onConfirm: onConfirm
      });
    } else if (window.confirm(opts.message || opts.title)) {
      onConfirm();
    }
  }

  var ACCEPT_TOAST_KEY = 'pm_accept_toast';

  // Floating confirmation after an accept. Accept navigates to the conversation
  // (full load), so the message is stashed in sessionStorage before navigating
  // and rendered on the next page load.
  function showAcceptToast(message) {
    var el = document.createElement('div');
    el.className = 'pm-accept-toast fixed -translate-x-1/2 z-[100] flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gray-900 text-white text-sm font-medium shadow-lg opacity-0 transition-opacity duration-300';
    el.setAttribute('role', 'status');
    el.innerHTML = '<i data-lucide="check-circle-2" class="w-4 h-4 text-emerald-400"></i><span></span>';
    el.querySelector('span').textContent = message;
    // Anchor near the top of the conversation pane (centered on it) so it never
    // covers the compose box at the bottom.
    var pane = document.getElementById('threads_right');
    if (pane) {
      var r = pane.getBoundingClientRect();
      el.style.left = Math.round(r.left + r.width / 2) + 'px';
      el.style.top = Math.round(r.top + 64) + 'px';
    } else {
      el.style.left = '50%';
      el.style.top = '72px';
    }
    document.body.appendChild(el);
    if (window.LucideIcons && typeof window.LucideIcons.initialize === 'function') {
      window.LucideIcons.initialize(el);
    }
    requestAnimationFrame(function () { el.classList.remove('opacity-0'); });
    setTimeout(function () {
      el.classList.add('opacity-0');
      setTimeout(function () { el.remove(); }, 350);
    }, 3000);
  }

  function maybeShowAcceptToast() {
    var msg = null;
    try {
      msg = sessionStorage.getItem(ACCEPT_TOAST_KEY);
      if (msg) sessionStorage.removeItem(ACCEPT_TOAST_KEY);
    } catch (e) { msg = null; }
    if (msg) showAcceptToast(msg);
  }

  // Request rows are not .pm_thread rows, so view_thread (which reads thread
  // metadata from the #thread_<id> DOM row) can't drive them directly. Inject a
  // hidden stub row carrying the data update_user_header needs, then reuse
  // view_thread for an in-page open (messages load by AJAX, no full reload).
  function ensureThreadStub(row) {
    var id = row.getAttribute('data-thread-id');
    if (!id || document.getElementById('thread_' + id)) return;
    var stub = document.createElement('div');
    stub.id = 'thread_' + id;
    stub.className = 'pm_thread hidden';
    stub.setAttribute('data-thread-id', id);
    stub.setAttribute('data-uid', row.getAttribute('data-uid') || '');
    stub.setAttribute('data-name', row.getAttribute('data-name') || '');
    stub.setAttribute('data-thumb', row.getAttribute('data-thumb') || '');
    stub.setAttribute('data-profile-link', row.getAttribute('data-profile-link') || '');
    var tl = document.getElementById('threads_left');
    if (tl) tl.appendChild(stub);
  }

  // Show the request action bar for the open conversation and hide the compose
  // box (no replying until the request is accepted).
  function showRequestBar(id, uid) {
    var bar = document.getElementById('message-request-bar');
    if (bar) {
      bar.setAttribute('data-thread-id', id);
      if (uid) bar.setAttribute('data-uid', uid);
      bar.classList.remove('hidden');
      if (window.LucideIcons && typeof window.LucideIcons.initialize === 'function') {
        window.LucideIcons.initialize(bar);
      }
    }
    var compose = document.getElementById('threads_right_bottom');
    if (compose) compose.classList.add('hidden');
    // Gate per-message react/reply until the request is accepted (no read
    // receipt). Report/delete stay available. CSS lives in pm/index.twig.
    var tr = document.getElementById('threads_right');
    if (tr) tr.classList.add('pm-request-readonly');
  }

  // Open a request conversation in-page (no full reload). Reading a Request
  // thread sends no read receipt (suppressed server-side).
  function openRequestPeek(row) {
    var id = row.getAttribute('data-thread-id');
    if (!id) return;
    if (typeof window.view_thread !== 'function') {
      window.location.href = '/app/messages?thread_id=' + encodeURIComponent(id);
      return;
    }
    ensureThreadStub(row);
    if (window.htm5_history_supported && window.history && window.history.pushState) {
      window.history.pushState({ thread_id: id }, null, '/app/messages?thread_id=' + id);
    }
    window.view_thread(id, true);
    showRequestBar(id, row.getAttribute('data-uid'));
  }

  // ───────────────────── inline preview ─────────────────────

  function getCount(sec) {
    return parseInt(sec.getAttribute('data-requests-count') || '0', 10);
  }

  function setCount(sec, count) {
    sec.setAttribute('data-requests-count', String(count));
    var a = sec.querySelector('#message-requests-count');
    var b = sec.querySelector('#message-requests-count-collapsed');
    if (a) a.textContent = String(count);
    if (b) b.textContent = String(count);
  }

  /**
   * Decrement the pending total by `removed`, update the banner badge, and hide
   * the banner when nothing is left.
   */
  function refreshCount(sec, removed) {
    if (!sec) return;
    var count = getCount(sec);
    if (removed) {
      count = Math.max(0, count - removed);
      setCount(sec, count);
    }
    if (count <= 0) {
      sec.remove();
    }
  }

  // Accepting moves the thread to Primary. Land in the accepted conversation so
  // the user sees it arrived in their Primary inbox (request controls gone,
  // normal composer shown). Decline/Block stay in the queue for triage.
  function goToPrimaryThread(id) {
    try {
      sessionStorage.setItem(ACCEPT_TOAST_KEY, t('common.pm.requests_accept_toast', 'Request accepted. Moved to your inbox.'));
    } catch (e) {}
    window.location.href = '/app/messages?thread_id=' + encodeURIComponent(id);
  }

  // Block / Delete on a request row reuse the regular thread-row controls via
  // the shared .thread-action-btn handler (messages.js → window.delete_thread:
  // block opens the block+report modal, delete the delete confirm). When the
  // backend removal completes, removeThreadRow fires 'pm:thread-removed' and
  // onThreadRemoved (below) drops the request row(s) and updates the count.
  function onThreadRemoved(id) {
    id = String(id);

    // If the removed thread is the open request conversation (peek or direct
    // load), hide the action bar and clear the pane so it doesn't linger behind
    // the block+report modal. Gated on the bar being visible so regular Primary
    // deletes (handled by removeThreadRow) are untouched.
    var barEl = document.getElementById('message-request-bar');
    var activeId = window.get_active_thread_id ? String(window.get_active_thread_id()) : null;
    if (barEl && !barEl.classList.contains('hidden') &&
        (barEl.getAttribute('data-thread-id') === id || activeId === id)) {
      barEl.classList.add('hidden');
      window.abandonPagedLoad?.();
      var right = document.getElementById('threads_right');
      if (right) right.innerHTML = '';
      var main = document.getElementById('threads_main');
      if (main) main.classList.remove('threads--open');
    }

    var removedAny = false;
    var sec = section();
    if (sec) {
      var secRow = sec.querySelector('.message-request-row[data-thread-id="' + id + '"]');
      if (secRow) { secRow.remove(); removedAny = true; }
    }
    var fvList = document.getElementById('message-requests-view-list');
    var fvRow = fvList ? fvList.querySelector('.message-request-row[data-thread-id="' + id + '"]') : null;
    if (fvRow) { fvRow.remove(); removedAny = true; }
    if (!removedAny) return;

    // The prefetched view HTML still contains the removed row.
    invalidatePrefetchedView();

    if (document.getElementById('message-requests-view-inner')) {
      fullViewChanged = true;
      fvSetCount(Math.max(0, fvGetCount() - 1));
      if (fvList && fvList.querySelectorAll('.message-request-row').length === 0) {
        if (fvSentinel()) {
          loadMoreRows();
        } else {
          fvShowEmpty();
        }
      }
    } else if (sec) {
      refreshCount(sec, 1);
    }
  }

  function onSectionClick(e) {
    // The section is a single banner that opens the dedicated Requests view.
    e.preventDefault();
    openFullView();
  }

  // ───────────────────── full "See all" view ─────────────────────

  // Requests-view HTML fetched during page idle so opening paints from memory
  // instead of waiting out a network round-trip (#3375). Exactly as fresh as
  // the rest of the page it was loaded with; invalidated whenever an action
  // mutates the queue, falling back to the on-click fetch.
  var prefetchedViewHtml = null;
  // Bumped on every invalidation so an in-flight prefetch that started before
  // a queue mutation cannot land its now-stale HTML afterwards.
  var prefetchGen = 0;

  function invalidatePrefetchedView() {
    prefetchedViewHtml = null;
    prefetchGen++;
  }

  function prefetchFullView() {
    if (!section() || fullView()) return;
    var gen = prefetchGen;
    fetch('/app/messages/requests-view', { credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.text();
      })
      .then(function (html) {
        if (gen !== prefetchGen) return;
        prefetchedViewHtml = (html && html.trim()) ? html : null;
      })
      .catch(function () {
        if (gen === prefetchGen) prefetchedViewHtml = null;
      });
  }

  function createFullViewOverlay(panel) {
    panel.classList.add('relative');
    // Bump the load-more generation so an in-flight page from a previous
    // overlay (closed then reopened) can't append into or stop this fresh one.
    fvGen++;
    var overlay = document.createElement('div');
    overlay.id = 'message-requests-view';
    overlay.className = 'absolute inset-0 z-30 bg-white dark:bg-gray-900 flex flex-col';
    panel.appendChild(overlay);
    return overlay;
  }

  function populateFullView(overlay, html) {
    overlay.innerHTML = html;
    fullViewChanged = false;
    if (window.LucideIcons && typeof window.LucideIcons.initialize === 'function') {
      window.LucideIcons.initialize(overlay);
    }
    watchLoadMoreSentinel();
  }

  // Placeholder shown in the pane the instant the Requests view opens, while
  // the view HTML is fetched (the cold path, when the idle prefetch hasn't
  // landed or was invalidated by a queue mutation). The header carries the
  // REAL back button (same id + handler) so the overlay is always dismissable
  // even if the fetch stalls — otherwise a hung request would trap the user on
  // an opaque overlay covering the inbox. The explainer strip and rows are
  // shimmer placeholders sized to match the real view so nothing jogs on swap.
  function skeletonHtml() {
    var row =
      '<div class="flex items-center gap-2 py-2.5 px-2">' +
        '<div class="w-[50px] h-[50px] rounded-full bg-gray-200 dark:bg-gray-700 flex-shrink-0"></div>' +
        '<div class="flex-1 min-w-0 space-y-2">' +
          '<div class="h-3.5 w-1/3 rounded bg-gray-200 dark:bg-gray-700"></div>' +
          '<div class="h-3 w-2/3 rounded bg-gray-100 dark:bg-gray-800"></div>' +
        '</div>' +
        '<div class="h-8 w-16 rounded-full bg-gray-200 dark:bg-gray-700 flex-shrink-0"></div>' +
      '</div>';
    var rows = '';
    for (var i = 0; i < 8; i++) rows += row;
    return '' +
      '<div class="flex flex-col h-full" aria-busy="true">' +
        '<div class="flex items-center gap-1 px-3 h-14 flex-none border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">' +
          '<button type="button" id="message-requests-view-back" ' +
                  'class="group no-btn-interactive flex items-center gap-2 flex-1 min-w-0 h-full text-left bg-transparent border-0 cursor-pointer rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-sky-500">' +
            '<i data-lucide="arrow-left" class="w-5 h-5 text-gray-500 flex-shrink-0 transition-transform duration-150 group-active:scale-90 motion-reduce:transition-none motion-reduce:group-active:scale-100"></i>' +
            '<span class="js-skeleton-back-label font-semibold text-gray-900 dark:text-gray-100"></span>' +
          '</button>' +
          '<div class="w-9 h-9 rounded-full bg-gray-100 dark:bg-gray-800 flex-shrink-0"></div>' +
        '</div>' +
        '<div class="flex-1 flex flex-col overflow-hidden animate-pulse motion-reduce:animate-none" aria-hidden="true">' +
          '<div class="px-3 py-2.5 flex-none bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">' +
            '<div class="h-4 w-64 max-w-full rounded bg-gray-200 dark:bg-gray-700"></div>' +
          '</div>' +
          '<div class="flex-1 overflow-hidden p-2 space-y-1">' + rows + '</div>' +
        '</div>' +
      '</div>';
  }

  // Paint the skeleton into the overlay: fill the back button's label from the
  // section banner (i18n-correct, no hardcoded copy) and initialise the Lucide
  // arrow so the functional back control renders.
  function showSkeleton(overlay) {
    overlay.innerHTML = skeletonHtml();
    var sec = section();
    var label = (sec && sec.getAttribute('data-back-label')) || '';
    if (label) {
      var lbl = overlay.querySelector('.js-skeleton-back-label');
      if (lbl) lbl.textContent = label;
      var btn = overlay.querySelector('#message-requests-view-back');
      if (btn) btn.setAttribute('aria-label', label);
    }
    if (window.LucideIcons && typeof window.LucideIcons.initialize === 'function') {
      window.LucideIcons.initialize(overlay);
    }
  }

  // ─────────────── keyset load-more (#3377) ───────────────
  // The first page renders up to page-size rows plus a sentinel; scrolling
  // near the sentinel fetches the next page of strictly-older rows
  // (?before=<last data-last-msg-id>) and appends it. The cursor is re-read
  // from the DOM each time, so rows declined meanwhile just shrink the page
  // boundary instead of skipping or duplicating rows.
  var fvLoadingMore = false;
  var fvObserver = null;
  // Incremented on every overlay open (createFullViewOverlay). A load-more
  // fetch captures the current value and drops its response if the overlay was
  // closed and reopened meanwhile, so a stale page can't corrupt the new one.
  var fvGen = 0;

  function fvSentinel() {
    return document.getElementById('message-requests-view-sentinel');
  }

  function fvStopLoadMore() {
    if (fvObserver) { fvObserver.disconnect(); fvObserver = null; }
    var sentinel = fvSentinel();
    if (sentinel) sentinel.remove();
  }

  function watchLoadMoreSentinel() {
    var sentinel = fvSentinel();
    if (!sentinel || typeof window.IntersectionObserver !== 'function') {
      if (sentinel) sentinel.remove();
      return;
    }
    fvObserver = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (entries[i].isIntersecting) { loadMoreRows(); break; }
      }
    }, { root: document.getElementById('message-requests-view-list'), rootMargin: '200px' });
    fvObserver.observe(sentinel);
  }

  function loadMoreRows() {
    if (fvLoadingMore) return;
    var list = document.getElementById('message-requests-view-list');
    var sentinel = fvSentinel();
    if (!list || !sentinel) return;

    var rows = list.querySelectorAll('.message-request-row[data-last-msg-id]');
    var cursor = rows.length ? rows[rows.length - 1].getAttribute('data-last-msg-id') : '';
    if (!cursor) { fvStopLoadMore(); return; }

    fvLoadingMore = true;
    var gen = fvGen;
    fetch('/app/messages/requests-view?before=' + encodeURIComponent(cursor), { credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.text();
      })
      .then(function (html) {
        if (gen !== fvGen) return; // overlay was closed/reopened; drop stale page
        fvLoadingMore = false;
        if (!fvSentinel()) return; // view closed or already stopped
        if (!html || !html.trim()) {
          fvStopLoadMore();
          if (!list.querySelector('.message-request-row')) fvShowEmpty();
          return;
        }
        var frag = document.createElement('template');
        frag.innerHTML = html;
        var added = frag.content.querySelectorAll('.message-request-row').length;
        sentinel.before(frag.content);
        if (window.LucideIcons && typeof window.LucideIcons.initialize === 'function') {
          window.LucideIcons.initialize(list);
        }
        var pageSize = parseInt(list.getAttribute('data-page-size') || '0', 10);
        if (!added || (pageSize > 0 && added < pageSize)) fvStopLoadMore();
      })
      .catch(function () {
        if (gen !== fvGen) return; // stale page from a since-closed overlay
        fvLoadingMore = false;
        // Leave the sentinel: scrolling again retries the page.
      });
  }

  function openFullView() {
    // Fill the threads (left) pane, not the conversation pane — the Requests
    // view replaces the list in place and works full-screen on mobile.
    var panel = document.getElementById('threads_left_container');
    if (!panel || fullView()) return;

    var overlay = createFullViewOverlay(panel);

    if (prefetchedViewHtml) {
      populateFullView(overlay, prefetchedViewHtml);
      return;
    }

    // Cold path: paint a skeleton at once so the pane responds to the tap while
    // the view HTML is fetched, then swap in the real content when it lands.
    showSkeleton(overlay);
    fetch('/app/messages/requests-view', { credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.text();
      })
      .then(function (html) {
        if (!overlay.isConnected) return; // view closed while loading
        // Empty view: a skeleton overlay has no observer, sentinel, or mutated
        // count, so drop it directly — no closeFullView bookkeeping needed.
        if (!html || !html.trim()) { overlay.remove(); return; }
        populateFullView(overlay, html);
      })
      .catch(function () {
        if (!overlay.isConnected) return;
        overlay.remove();
        actionError();
      });
  }

  function closeFullView() {
    // fvSetCount keeps the section banner count in sync while the view is
    // open, so closing needs no reload — just drop the banner when nothing
    // is left to triage. (Accepting navigates away and never comes back
    // through here; declines/blocks only ever shrink the Requests queue.)
    if (fvObserver) { fvObserver.disconnect(); fvObserver = null; }
    fvLoadingMore = false;
    var overlay = fullView();
    if (overlay) overlay.remove();
    if (fullViewChanged) {
      var sec = section();
      if (sec && getCount(sec) <= 0) sec.remove();
      fullViewChanged = false;
    }
  }

  function fvGetCount() {
    var inner = document.getElementById('message-requests-view-inner');
    return inner ? parseInt(inner.getAttribute('data-requests-count') || '0', 10) : 0;
  }

  function fvSetCount(count) {
    var inner = document.getElementById('message-requests-view-inner');
    if (inner) inner.setAttribute('data-requests-count', String(count));
    var badge = document.getElementById('message-requests-view-count');
    if (badge) badge.textContent = String(count);
    var sec = section();
    if (sec) setCount(sec, count);
  }

  function fvShowEmpty() {
    fvStopLoadMore();
    var list = document.getElementById('message-requests-view-list');
    var empty = document.getElementById('message-requests-view-empty');
    if (list) list.classList.add('hidden');
    if (empty) {
      empty.classList.remove('hidden');
      empty.classList.add('flex');
    }
  }

  // ─────────────── bulk decline-all (#3664) ───────────────
  // ThreadService::DECLINE_ALL_MAX. The server declines at most this many per
  // call and leaves the rest queued, so a batch that comes back full means more
  // remain and the endpoint has to be called again. Only a SHORT batch proves
  // the queue is empty.
  var DECLINE_ALL_MAX = 1000;

  // Ceiling on those re-calls so a pathological backlog can't spin. Matches the
  // SPA's DECLINE_ALL_ROUNDS, which drives the same endpoint.
  var DECLINE_ALL_ROUNDS = 10;

  // Held for the whole multi-round run. Read synchronously, because the rounds
  // span several round-trips and reopening the overflow menu meanwhile would
  // otherwise start a second loop against the same queue.
  var fvDecliningAll = false;

  // Re-render the Requests view from the server after a bulk decline that left
  // rows behind, so the list and both counts show the real remainder instead of
  // an empty state. The banner count is floored at 1 first: closeFullView drops
  // the section at zero, which would take away the entry point to a queue that
  // still has requests in it. The reopened view is server-rendered, so its own
  // count replaces this placeholder.
  function fvReopenWithRemainder(declined) {
    fvSetCount(Math.max(1, fvGetCount() - declined));
    closeFullView();
    openFullView();
  }

  function fvDeleteAll() {
    confirmThen({
      title: t('common.pm.requests_delete_all_confirm_title', 'Delete all requests?'),
      message: t('common.pm.requests_delete_all_confirm_body', "They'll be removed from your inbox. Nobody is notified. If someone messages again, their new message comes back to Requests."),
      confirmText: t('common.pm.requests_delete_all', 'Delete all')
    }, function () {
      if (fvDecliningAll) return;
      fvDecliningAll = true;

      // Grey the rows for the duration, not the whole overlay: the header keeps
      // the back button live so a slow run can still be left.
      var list = document.getElementById('message-requests-view-list');
      setBusy(list, true);

      // Total across every round. Kept out here so a round that fails after
      // earlier ones committed can still resync from what the server deleted.
      var declined = 0;

      function done() {
        fvDecliningAll = false;
        setBusy(list, false);
      }

      // Resolves true when a batch came back short (queue drained), false when
      // the round ceiling was reached on a still-full batch (requests remain).
      function round(n) {
        return apiPost('/thread/decline-all')
          .then(function (resp) { return resp.json(); })
          .then(function (payload) {
            var batch = (payload && payload.data && parseInt(payload.data.declined, 10)) || 0;
            declined += batch;
            if (batch < DECLINE_ALL_MAX) {
              return true;
            }
            return n + 1 < DECLINE_ALL_ROUNDS ? round(n + 1) : false;
          });
      }

      round(0)
        .then(function (drained) {
          fullViewChanged = true;
          // The prefetched view HTML still lists the declined rows.
          invalidatePrefetchedView();
          done();
          if (!drained) {
            fvReopenWithRemainder(declined);
            return;
          }
          // Declining requests never touches the Primary list, so show the
          // empty state in place; the section banner drops on close via the
          // fvSetCount(0) sync + closeFullView's zero-count removal.
          fvSetCount(0);
          fvShowEmpty();
        })
        .catch(function () {
          invalidatePrefetchedView();
          done();
          if (declined > 0) {
            fullViewChanged = true;
            fvReopenWithRemainder(declined);
          }
          actionError();
        });
    });
  }

  function onFullViewClick(e) {
    var menu = document.getElementById('message-requests-view-menu');
    if (e.target.closest('#message-requests-view-menu-btn')) {
      e.preventDefault();
      if (menu) menu.classList.toggle('hidden');
      return;
    }
    if (e.target.closest('#message-requests-view-delete-all')) {
      e.preventDefault();
      if (menu) menu.classList.add('hidden');
      fvDeleteAll();
      return;
    }
    if (e.target.closest('#message-requests-view-back')) {
      e.preventDefault();
      closeFullView();
      return;
    }

    var row = e.target.closest('.message-request-row');
    if (!row) return;
    var id = row.getAttribute('data-thread-id');
    if (!id) return;

    if (e.target.closest('.message-request-accept')) {
      e.preventDefault();
      setBusy(row, true);
      apiPost('/thread/' + id + '/accept')
        .then(function () { goToPrimaryThread(id); })
        .catch(function () { setBusy(row, false); actionError(); });
      return;
    }
    // Block / Delete (.thread-action-btn) and the swipe actions are wired by
    // messages.js — don't treat those clicks as a row-open.
    if (e.target.closest('.thread-action-btn') || e.target.closest('.thread-swipe-actions')) {
      return;
    }

    // Row body opens the conversation in-page (the full view stays open in the
    // threads pane on desktop, and is covered by the conversation on mobile).
    openRequestPeek(row);
  }

  // Request action bar inside an opened request conversation (#3039).
  // Accept keeps the now-Primary conversation open (reload). Decline/Block
  // remove the request, so return to the thread list.
  function onBarClick(e, bar) {
    var id = bar.getAttribute('data-thread-id');
    if (!id) return;

    if (e.target.closest('#message-request-bar-accept')) {
      e.preventDefault();
      setBusy(bar, true);
      apiPost('/thread/' + id + '/accept')
        .then(function () { goToPrimaryThread(id); })
        .catch(function () { setBusy(bar, false); actionError(); });
      return;
    }
    // Block / Delete reuse the shared thread controls: block opens the
    // block+report modal, delete the delete confirm. removeThreadRow + the
    // pm:thread-removed listener handle closing the peek and dropping the row.
    if (e.target.closest('#message-request-bar-block')) {
      e.preventDefault();
      if (typeof window.delete_thread === 'function') window.delete_thread(bar, id, 1);
      return;
    }
    if (e.target.closest('#message-request-bar-decline')) {
      e.preventDefault();
      if (typeof window.delete_thread === 'function') window.delete_thread(bar, id, 0);
      return;
    }
  }

  function onClick(e) {
    // Close the Requests overflow menu on any click outside it or its trigger
    // (document-level, mirroring the PM options-menu pattern in messages.js).
    var rMenu = document.getElementById('message-requests-view-menu');
    if (rMenu && !rMenu.classList.contains('hidden') &&
        !e.target.closest('#message-requests-view-menu-btn') &&
        !e.target.closest('#message-requests-view-menu')) {
      rMenu.classList.add('hidden');
    }

    var bar = document.getElementById('message-request-bar');
    if (bar && bar.contains(e.target)) {
      onBarClick(e, bar);
      return;
    }
    var fv = fullView();
    if (fv && fv.contains(e.target)) {
      onFullViewClick(e);
      return;
    }
    var sec = section();
    if (sec && sec.contains(e.target)) {
      onSectionClick(e);
    }
  }

  // Premium self-serve toggle for the inbox split (#3270), wired from the PM
  // options menu. Persists via the /v1 settings endpoint, then reloads so the
  // server re-renders the inbox in the new mode. The body must be
  // form-encoded: the settings validator reads the POST parameter bag, so a
  // JSON body would be silently dropped.
  window.toggle_message_requests = function (enable) {
    if (!window.ApiClient || typeof window.ApiClient.request !== 'function') {
      return false;
    }

    var meta = document.querySelector('meta[name="user-id"]');
    var userId = meta ? String(meta.getAttribute('content') || '') : '';
    if (!userId) {
      return false;
    }

    window.ApiClient.request('/settings/' + userId, {
      method: 'PUT',
      body: new URLSearchParams({ message_requests: enable ? 'true' : 'false' })
    }).then(function (resp) {
      if (!resp || !resp.ok) {
        throw new Error('Request failed: ' + (resp ? resp.status : 'no response'));
      }
      window.location.reload();
    }).catch(function () {
      actionError();
    });

    return false;
  };

  function init() {
    maybeShowAcceptToast();
    // Warm the full-view HTML once the page is idle, so tapping the banner
    // paints from memory. Only when there is a Requests section to open.
    if (section()) {
      if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(prefetchFullView, { timeout: 3000 });
      } else {
        window.setTimeout(prefetchFullView, 1500);
      }
    }
  }

  document.addEventListener('click', onClick);
  // Block / Delete on a request go through the shared thread controls; sync the
  // request list + count when the backend removal completes.
  document.addEventListener('pm:thread-removed', function (e) {
    onThreadRemoved(e.detail && e.detail.threadId);
  });
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
